import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import {
  Key,
  Plus,
  Book,
  ExternalLink
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import api from '../../lib/api'

// Import the existing ApiKeys functionality
import ApiKeys from './ApiKeys'

export default function ApiManagement() {
  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.get('/auth/whoami').then((res) => res.data),
  })

  const baseUrl = process.env.NODE_ENV === 'production'
    ? 'https://text.teamredge.com'
    : 'http://localhost:8011'

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5" />
            API Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="prose max-w-none">
            <p className="text-gray-700 leading-relaxed">
              Manage your API keys for accessing the redgeGate API.
              Generate new keys, monitor usage, and maintain secure access to your SMS services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Key className="h-5 w-5 text-blue-600" />
                Quick Actions
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Generate new API keys for your applications
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Monitor API usage and track requests
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-700">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  Revoke old or unused API keys
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Book className="h-5 w-5 text-green-600" />
                Documentation
              </h3>
              <div className="space-y-3">
                <p className="text-sm text-gray-700">
                  Need help with the API? Check out our comprehensive documentation with examples.
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.open('/api-docs', '_blank')}
                >
                  <Book className="h-4 w-4 mr-2" />
                  View API Documentation
                  <ExternalLink className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">Base URL</h4>
            <div className="flex items-center justify-between bg-white p-3 rounded border">
              <code className="text-sm font-mono">{baseUrl}/api</code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigator.clipboard.writeText(baseUrl + '/api')}
              >
                <Key className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* API Keys Management */}
      <ApiKeys />
    </div>
  )
}